import { useState, useMemo, useLayoutEffect } from 'react';
import { omit, isEmpty } from 'mojito/utils';
import MojitoCore from 'mojito/core';
import Text from 'presentation/components/text/index.js';
import SelectableButton from 'presentation/components/selectable-button/index.js';
import FlexPane from 'presentation/components/flex-pane/index.js';
import RotatableImage from 'presentation/components/rotatable-image/index.js';
import Button from 'presentation/components/button/index.js';

const { useResizeDetector } = MojitoCore.Base.resizeDetector;
const { merge } = MojitoCore.Base.objUtils;
const classUtils = MojitoCore.Base.classUtils;

export default function ButtonBar(props) {
    const { config, style, stringResolver } = props.mojitoTools;

    const itemsProps = useMemo(
        () => ({
            ...omit(props, 'mojitoTools'),
            style,
            config,
            stringResolver,
        }),
        [props, style, config, stringResolver]
    );

    const [isExpanded, setIsExpanded] = useState(config.initiallyExpanded);
    const [shouldShowExpandButton, setShouldShowExpandButton] = useState(false);

    const { elementRef, element, width } = useResizeDetector();

    useLayoutEffect(() => {
        setShouldShowExpandButton(isContentOverflown(element));
    }, [width, element, props.items.length]);

    const onClickExpandButton = () => {
        props.onSHowHideToggleClicked(!isExpanded);
        setIsExpanded(isExpanded => !isExpanded);
    };

    let containerConfig;

    if (config.enableExpandCollapse) {
        containerConfig = isExpanded
            ? merge(config.container, config.containerExpanded)
            : merge(config.container, config.containerCollapsed);
    } else {
        containerConfig = config.container;
    }

    return (
        <FlexPane class={classUtils.classNames('ButtonBar', props.class)} config={containerConfig}>
            <FlexPane config={config.buttonWrapper} elementRef={elementRef}>
                <MenuItems {...itemsProps} />
            </FlexPane>
            {config.enableExpandCollapse && shouldShowExpandButton && (
                <ExpandButton
                    {...itemsProps}
                    isExpanded={isExpanded}
                    onClick={onClickExpandButton}
                />
            )}
        </FlexPane>
    );
}

ButtonBar.getStyle = (config, applicationMode, merge) => {
    const { style } = config;

    const newFeature = {
        pointerEvents: 'none',
        position: 'relative',
        top: '-8px',
        right: '-8px',
        ...style.newFeature,
    };

    return {
        newFeature: newFeature,
        newFeatureSelected: merge(newFeature, style.newFeatureActive),
    };
};

const MenuItems = props => {
    const { items } = props;
    return items.map(item => {
        const key = item.key || item.label;
        return <MenuButton {...props} key={key} item={item} />;
    });
};

const MenuButton = props => {
    const { item, getItemHrefLink, selectedKey, onSelectionChanged, config } = props;
    const { key, label, newFeature } = item;
    const isSelected = key === selectedKey;
    const onItemClicked = (event, data) => {
        const { key: itemKey, label: itemLabel } = data;
        onSelectionChanged(itemKey, itemLabel);
    };

    return (
        <SelectableButton
            class={`ta-ButtonBarItem${isSelected ? ' ta-selected' : ''}`}
            config={config.button}
            selected={isSelected}
            onClick={onItemClicked}
            onClickData={{ key, label }}
            hrefLink={getItemHrefLink(key)}
        >
            <Text config={config.text} class="ta-ButtonBarItemText">
                {label}
            </Text>
            {newFeature && <NewFeature {...props} isSelected={isSelected} />}
        </SelectableButton>
    );
};

const NewFeature = props => {
    const { isSelected, newFeatureText, style } = props;
    const currentStyle = isSelected ? style.newFeatureSelected : style.newFeature;

    return (
        <div style={currentStyle} className="ta-NewFeatureSign">
            {newFeatureText}
        </div>
    );
};

const ExpandButton = props => {
    const { config, stringResolver, isExpanded, onClick } = props;
    const l10nTextKey = isExpanded ? '$BUTTON_BAR.HIDE_TEXT' : '$BUTTON_BAR.MORE_TEXT';

    return (
        <Button class="ta-expandButton" onClick={onClick} config={config.expandButton}>
            <Text config={config.expandButtonText} class="ta-ExpandButtonText">
                {stringResolver.resolveString(l10nTextKey)}
            </Text>
            <RotatableImage
                config={config.expandButtonIcon}
                angle={isExpanded ? '0deg' : '180deg'}
            />
        </Button>
    );
};

const isContentOverflown = el => {
    if (isEmpty(el?.children)) {
        return false;
    }

    const elementsCollection = el.children;

    const firstElement = elementsCollection[0];
    const lastElement = elementsCollection[elementsCollection.length - 1];

    return lastElement.offsetTop > firstElement.offsetTop;
};
